// Session 67: the help assistant, a floating button on every signed-in page.
//
// Talks to /api/help (Cloudflare Pages Function, SSE) with the user's
// Supabase access token and a little context: which page, which client,
// which role. "Ask Leena" sends the conversation to the agency by email.
// The chat is kept in sessionStorage per user and client, so it survives
// navigation but not a new tab. Styles: .helpw in styles.css.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../supabase.js';
import { useAuth } from '../AuthContext.jsx';

const WELCOME = {
  role: 'assistant',
  content: "Hi, I'm the portal assistant. Ask me how anything here works, where to find something, or what to do next. If I can't help, press Ask Leena and she gets this conversation.",
};

// A readable name for the page the user is on, for the prompt and the email.
const PAGE_LABELS = [
  ['/admin/clients/new', 'Add client'],
  ['/admin/clients', 'Clients (agency)'],
  ['/admin/signups', 'Sign-ups (agency)'],
  ['/admin', 'Admin'],
  ['/welcome', 'Your details'],
  ['/plan', 'Plan'],
  ['/blogs', 'Blogs'],
  ['/strategy', 'Strategy'],
  ['/newsletters', 'Newsletters'],
  ['/results', 'Results'],
  ['/courses', 'AI courses'],
  ['/learn', 'Learn'],
  ['/gmb-audit', 'Google Business Profile'],
  ['/seo-recommendations', 'SEO recommendations'],
  ['/', 'Home'],
];
function pageLabel(path) {
  const hit = PAGE_LABELS.find(([p]) => (p === '/' ? path === '/' : path.startsWith(p)));
  if (!hit) return path;
  if (hit[0] === '/blogs' && path.endsWith('/review')) return 'Blog review';
  if (hit[0] === '/blogs' && path.endsWith('/questions')) return 'Blog questions';
  return hit[1];
}

function storageKey(userId, clientId) {
  return `lotus_help:${userId || 'anon'}:${clientId || 'none'}`;
}
function loadSaved(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed.messages)) return parsed;
  } catch { /* corrupted state falls through to a fresh chat */ }
  return null;
}

// Links in replies: portal paths stay in-tab, anything else opens a new tab.
function Linkified({ text }) {
  const parts = String(text).split(/(https?:\/\/[^\s)]+)/g);
  return parts.map((part, i) => {
    if (!/^https?:\/\//.test(part)) return part;
    let internal = false;
    try { internal = new URL(part).host === window.location.host; } catch { /* leave external */ }
    const href = internal ? part.replace(/^https?:\/\/[^/]+/, '') || '/' : part;
    return (
      <a key={i} href={href} target={internal ? undefined : '_blank'} rel={internal ? undefined : 'noopener noreferrer'}>
        {part.replace(/^https?:\/\/(www\.)?/, '')}
      </a>
    );
  });
}

export default function HelpWidget() {
  const { session, activeClient, isAgency } = useAuth();
  const location = useLocation();
  const userId = session?.user?.id || null;
  const clientId = activeClient?.client_id || null;
  const key = storageKey(userId, clientId);

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => loadSaved(key)?.messages || [WELCOME]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);   // the Ask Leena note box
  const [note, setNote] = useState('');
  const conversationId = useRef(loadSaved(key)?.conversationId || null);
  const logRef = useRef(null);
  const inputRef = useRef(null);

  // A different user or client: load that chat (or a fresh one).
  useEffect(() => {
    const saved = loadSaved(key);
    setMessages(saved?.messages || [WELCOME]);
    conversationId.current = saved?.conversationId || null;
    setAsking(false); setNote('');
  }, [key]);

  useEffect(() => {
    try {
      sessionStorage.setItem(key, JSON.stringify({ conversationId: conversationId.current, messages }));
    } catch { /* storage full or blocked; the chat still works for this page */ }
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [messages, key]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const onKeyDown = useCallback((e) => { if (e.key === 'Escape') setOpen(false); }, []);

  function context() {
    return {
      path: location.pathname,
      pageLabel: pageLabel(location.pathname),
      clientId: clientId || '',
      clientName: activeClient?.name || '',
      role: isAgency ? 'agency' : 'client',
    };
  }

  async function authHeaders() {
    const { data: { session: s } } = await supabase.auth.getSession();
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${s?.access_token || ''}` };
  }

  const setBotText = (updater) =>
    setMessages((m) => {
      const next = [...m];
      const last = next[next.length - 1];
      next[next.length - 1] = { ...last, content: updater(last.content), pending: false };
      return next;
    });

  async function sendMessage(e) {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    setBusy(true);
    setMessages((m) => [...m, { role: 'user', content: text }, { role: 'assistant', content: '', pending: true }]);
    try {
      const res = await fetch('/api/help', {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ conversationId: conversationId.current, message: text, context: context() }),
      });
      if (!res.ok) {
        let msg = 'Something went wrong. Please try again in a moment.';
        try { msg = (await res.json()).message || msg; } catch { /* keep default */ }
        setBotText(() => msg);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let gotText = false;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop();
        for (const evt of events) {
          const line = evt.split('\n').find((l) => l.startsWith('data: '));
          if (!line) continue;
          let data;
          try { data = JSON.parse(line.slice(6)); } catch { continue; }
          if (data.type === 'conversation') conversationId.current = data.id;
          else if (data.type === 'text') { gotText = true; setBotText((c) => c + data.text); }
          else if (data.type === 'error' && !gotText) setBotText(() => data.message);
        }
      }
      if (!gotText) setBotText((c) => c || 'Sorry, no reply came through. Please try again.');
    } catch {
      setBotText(() => 'Connection trouble. Please try again, or email leena@digitallotusmarketing.com.');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.focus();
    }
  }

  async function askLeena() {
    if (busy) return;
    if (!conversationId.current) {
      setMessages((m) => [...m, { role: 'assistant', content: 'Type your question first, so Leena sees what it is about. Then press Ask Leena.' }]);
      return;
    }
    setBusy(true);
    try {
      const res = await fetch('/api/help', {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ conversationId: conversationId.current, escalate: true, note: note.trim(), context: context() }),
      });
      let data = {};
      try { data = await res.json(); } catch { /* fall through */ }
      setMessages((m) => [...m, { role: 'assistant', content: data.message || (res.ok ? 'Sent to Leena.' : 'This could not be sent just now. Please email leena@digitallotusmarketing.com.') }]);
      if (res.ok) { setAsking(false); setNote(''); }
    } catch {
      setMessages((m) => [...m, { role: 'assistant', content: 'Connection trouble. Please email leena@digitallotusmarketing.com.' }]);
    } finally {
      setBusy(false);
    }
  }

  function newChat() {
    conversationId.current = null;
    setMessages([WELCOME]);
    setAsking(false); setNote('');
  }

  if (!session) return null;

  return (
    <div className="helpw" onKeyDown={onKeyDown}>
      {open && (
        <div className="helpw-panel" role="dialog" aria-label="Portal help">
          <div className="helpw-header">
            <div>
              <div className="helpw-title">Need a hand?</div>
              <div className="helpw-subtitle">{activeClient?.name ? `Help for ${activeClient.name}` : 'Help with the portal'}</div>
            </div>
            <div className="helpw-header-actions">
              <button type="button" className="helpw-link" onClick={newChat} disabled={busy}>New chat</button>
              <button type="button" className="helpw-close" aria-label="Close help" onClick={() => setOpen(false)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
          </div>

          <div className="helpw-log" ref={logRef} role="log" aria-live="polite">
            {messages.map((m, i) => (
              <div key={i} className={`helpw-msg helpw-msg-${m.role}`}>
                {m.pending ? (
                  <span className="helpw-typing" aria-label="Assistant is typing"><span /><span /><span /></span>
                ) : (
                  <Linkified text={m.content} />
                )}
              </div>
            ))}
          </div>

          {asking ? (
            <div className="helpw-ask">
              <label className="helpw-ask-label" htmlFor="helpw-note">Anything to add for Leena? (optional)</label>
              <textarea id="helpw-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} />
              <div className="helpw-ask-actions">
                <button type="button" className="btn" onClick={() => setAsking(false)} disabled={busy}>Back</button>
                <button type="button" className="btn btn-primary" onClick={askLeena} disabled={busy}>Send to Leena</button>
              </div>
            </div>
          ) : (
            <form className="helpw-inputrow" onSubmit={sendMessage}>
              <input ref={inputRef} className="helpw-input" type="text" value={input} onChange={(e) => setInput(e.target.value)}
                     placeholder="Ask how something works..." maxLength={2000} aria-label="Your question" />
              <button className="helpw-send" type="submit" disabled={busy || !input.trim()} aria-label="Send">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
              </button>
            </form>
          )}
          <div className="helpw-footnote">
            AI assistant, cannot see your account.{' '}
            {!asking && <button type="button" className="helpw-link helpw-link-ask" onClick={() => setAsking(true)} disabled={busy}>Ask Leena</button>}
          </div>
        </div>
      )}

      <button className={`helpw-fab ${open ? 'helpw-fab-open' : ''}`} aria-label={open ? 'Close help' : 'Need a hand?'} aria-expanded={open}
              onClick={() => setOpen((o) => !o)}>
        {open ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        ) : (
          <span className="helpw-fab-mark" aria-hidden="true">?</span>
        )}
      </button>
    </div>
  );
}
